import type { Language } from '@luna/i18n';
import type { Method } from '@luna/api';
import { request } from '@luna/api';
import { getEnv } from '@luna/env';
import { getToken } from '@shared/auth';
import { logger } from '@luna/logger';

import type { RevokeCallback } from './BlobService';
import { openBlobInBrowserTab, saveBlob } from './BlobService';
import { TARGET } from './types';
import { getFilenameFromContentDisposition } from './getFilenameFromContentDisposition';

const ACTION_ATTRIBUTE = 'action';
const METHOD_ATTRIBUTE = 'method';
const DISPLAY_NONE = 'none';
const TYPE_ATTRIBUTE = 'type';
const TYPE_HIDDEN = 'hidden';
const NAME_ATTRIBUTE = 'name';
const ACCESS_TOKEN = 'access_token';
const VALUE_ATTRIBUTE = 'value';
const WEB_API_KEY = 'WEB-API-key';
const CLEANUP_TIMEOUT = 500;
const TARGET_ATTRIBUTE = 'target';
const CLIENT_ACCEPT_LANGUAGE = 'headers.Client-Accept-Language';

type SaveFileUnderName = string | ((response: Response) => string) | undefined;

interface DownloadFileSettings {
    url: string;
    saveFileUnderName?: SaveFileUnderName;
    method?: Method;
    body?: Record<string, unknown>;
    options?: Record<string, unknown>;

    success?(): void;

    error?(err: any): void;
}

export function getFilenameFromResponse(contentDisposition?: string): string {
    const filenameFromResponse = getFilenameFromContentDisposition(contentDisposition);

    if (!filenameFromResponse) {
        // The response must have these headers:
        // Access-Control-Expose-Headers: Content-Disposition
        // Content-Disposition: attachment; filename=Whatever-Document.pdf; filename*=UTF-8''Whatever-Document.pdf
        logger.error('Expected a filename for the downloaded file.');
        throw new Error('Expected a filename for the downloaded file.');
    }

    return filenameFromResponse;
}

const getFilename = (saveFileUnderName: SaveFileUnderName, response: Response): string => {
    if (typeof saveFileUnderName === 'function') {
        return saveFileUnderName(response);
    }

    if (saveFileUnderName) {
        return saveFileUnderName;
    }

    return getFilenameFromResponse(response.headers.get('content-disposition') || undefined);
};

export async function saveFileFromResponse(
    response: Response,
    saveFileUnderName?: SaveFileUnderName,
): Promise<void> {
    const fileName = getFilename(saveFileUnderName, response);

    const contentType = response.headers.get('content-type');
    if (!contentType) {
        logger.error(`Missing Content-Type header for the request to download file: ${fileName}`);
    }

    const blob = await response.blob();

    saveBlob(blob, fileName);
}

export async function downloadFile({
    url,
    saveFileUnderName,
    method = 'POST',
    body,
    success,
    error,
    options = {},
}: DownloadFileSettings): Promise<void> {
    try {
        const response = await request(url, {
            body,
            method,
            skipParseJSON: true,
            ...options,
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
                ...((options?.headers as Record<string, string>) || {}),
            },
        });

        await saveFileFromResponse(response, saveFileUnderName);

        success?.();
    } catch (err) {
        error?.(err);
    }
}

interface OpenFileInBrowserTabSettings {
    url: string;
    method?: Method;
    body?: Record<string, unknown>;
    options?: Record<string, unknown>;
}

export async function openFileInBrowserTab({
    url,
    method = 'GET',
    body,
    options = {},
}: OpenFileInBrowserTabSettings): Promise<RevokeCallback> {
    const response = await request(url, {
        body,
        method,
        skipParseJSON: true,
        ...options,
    });

    const blob = await response.blob();

    return openBlobInBrowserTab(blob);
}

function appendFormInput(form: HTMLFormElement, attributes: { name: string; value: string }): void {
    const { name, value } = attributes;
    const input = document.createElement('input');
    input.setAttribute(TYPE_ATTRIBUTE, TYPE_HIDDEN);
    input.setAttribute(NAME_ATTRIBUTE, name);
    input.setAttribute(VALUE_ATTRIBUTE, value);

    form.appendChild(input);
}

export function downloadFileByForm(
    downloadUrl: string,
    options: { method?: string; target?: TARGET; acceptLanguage?: Language } = {},
): void {
    const form = document.createElement('form');
    form.setAttribute(ACTION_ATTRIBUTE, downloadUrl);
    form.setAttribute(METHOD_ATTRIBUTE, options.method || 'POST');
    form.setAttribute(TARGET_ATTRIBUTE, options.target || TARGET.SELF);
    form.style.display = DISPLAY_NONE;
    document.body.appendChild(form);

    appendFormInput(form, { name: ACCESS_TOKEN, value: getToken() });

    const { webAPIClientId } = getEnv();
    if (webAPIClientId) {
        appendFormInput(form, { name: WEB_API_KEY, value: webAPIClientId });
    }

    if (options.acceptLanguage) {
        appendFormInput(form, { name: CLIENT_ACCEPT_LANGUAGE, value: options.acceptLanguage });
    }

    form.submit();

    setTimeout(() => {
        document.body.removeChild(form);
    }, CLEANUP_TIMEOUT);
}
